import Blank, { BLANK_INPUT_MIN_WIDTH } from '@doltech/ui/lib/figma/BlankEditor/DoTest/Blank';
import { withStoreRequiredForEditor } from '@doltech/ui/lib/figma/BlogEditor/core/withStoreRequiredForEditor';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { DolEditor } from '@doltech/ui/lib/figma/DolEditor/DolEditor';
import { EDITABLE_BLANK_VOID } from '@doltech/ui/lib/figma/DolEditor/elements/editable-blank-voids/types';
import { tableDatav1ToV2 } from '@doltech/ui/lib/figma/DolEditor/elements/table-blog/transforms/tableV1ToV2';
import { compose } from '@doltech/utils/lib/compose';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import TextAreaAutoResize from 'react-textarea-autosize-2';
import styled from 'styled-components';

import { DoTestTableCompletionInstance } from '../student-do-test/model/Question/do-test-table-completion.store';
import { useTestSubmissionStore } from '../student-do-test/model/test-submissions.hook';
import { SingleAnswerMain } from './SingleAnswer';
import { useTableCompletionPluginAndEditorChains } from './useTableCompletionPluginAndEditorChains';
import { ToolbarDoingTestV2 } from '@doltech/ui/lib/figma/ToolbarDoingTestV2/ToolbarDoingTestV2';

export interface TableCompletionProps extends React.HTMLAttributes<HTMLDivElement> {
  data: DoTestTableCompletionInstance;
}

const Main = styled(SingleAnswerMain)`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  overflow: auto;
`;

export const TableCompletion = compose(
  withStoreRequiredForEditor({}),
  observer
)((props: TableCompletionProps) => {
  const { data, ...rest } = props;
  const store = useTestSubmissionStore();
  const editorBlanks = React.useMemo(() => {
    if (data) {
      return data.blanks.map((bl, idx) => ({ blankId: bl.id, blankIndex: idx }));
    }
    return [];
  }, [data]);
  const no = data?.no;
  const { plugins, editorChains } = useTableCompletionPluginAndEditorChains({ editorBlanks, no });
  if (!data) return null;
  const { editorValue, question, userAnswers, blanks, onAnswerQuestion, setEditorValue } = data;
  const getUserAnswers = React.useCallback(
    (blankId: string) => {
      return userAnswers.find((ans) => ans.blankId === blankId)?.userAnswer;
    },
    [userAnswers]
  );

  const initialValue = React.useMemo(() => {
    return tableDatav1ToV2(JSON.parse(editorValue));
  }, [editorValue]);

  return (
    <Main className="dol-question single-answer" {...rest}>
      {question && <Title>{question}</Title>}

      <DolEditor
        readOnly
        initialValue={initialValue}
        editorChains={editorChains}
        onEditorChange={(value) => setEditorValue(JSON.stringify(value))}
        withToolbar={<ToolbarDoingTestV2 />}
        plugins={plugins}
        renderElementDeps={[blanks]}
        renderElement={[
          (renderProps) => {
            if (renderProps.element.type === EDITABLE_BLANK_VOID) {
              const blankId = renderProps.element.children[0].blankId as string;
              const blank = editorBlanks.find((bl) => bl.blankId === blankId);
              return (
                <Blank
                  showRemoveButton={false}
                  input={<AutoResizeInputStyled parentSelector="td" siblingWidth={48} />}
                  id={blankId}
                  no={no + blank.blankIndex}
                  value={getUserAnswers(blankId)}
                  onRemoveBlank={() => {
                    if (store.preventUserAction()) return;
                    onAnswerQuestion({ blankId, text: null });
                  }}
                  onChange={(input) => {
                    if (store.preventUserAction()) return;
                    onAnswerQuestion(input);
                  }}
                />
              );
            }

            return undefined;
          },
        ]}
      />
    </Main>
  );
});

const AutoResizeInputStyled = styled(TextAreaAutoResize)`
  textarea {
    background-color: transparent;
    padding: 0;
    border: none;
    outline: none;
    width: auto;
    color: ${colorsV2.black100};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    border-radius: 0;
    border-bottom: 2px solid ${colorsV2.black10};
    min-width: ${BLANK_INPUT_MIN_WIDTH}px;
    vertical-align: middle;
    &:focus {
      border-bottom: 2px solid ${colorsV2.blue80};
    }
    &::selection {
      background: ${colorsV2.blue40};
      color: ${colorsV2.black100};
    }
  }
`;

const Title = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: ${colorsV2.black100};
  margin-bottom: 24px;
`;
